// Lightweight fetch wrapper for the public-facing site. Talks to the
// apex-hosted PublicSiteController at /api/site/v1/*.
//
// No longer anonymous: as of 2026-08-04 the site requires a DorkNet sign-in
// with in-game credentials, so every request carries a bearer token. The
// /join endpoints remain anonymous server-side and simply send no header
// when there is no session.
//
// A 401 clears the session and notifies, so <App> falls back to the login
// screen instead of pages rendering half-empty.

import { clearSession, getToken } from './auth';

const BASE = '/api/site/v1';

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = getToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function get<T = unknown>(path: string): Promise<T> {
  const res = await fetch(BASE + path, { headers: authHeaders() });
  if (res.status === 401) {
    clearSession();
    throw new ApiError(401, 'Session expired — sign in again.');
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      else if (body?.message) msg = body.message;
    } catch { /* keep status text */ }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// POST with a JSON body. Used by the /join signup-code flow — the only
// write path the otherwise read-only public site has. On a non-2xx the
// server's { error } string (e.g. "code_expired") is surfaced as the
// thrown ApiError message so the page can map it to friendly copy.
export async function post<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    clearSession();
    throw new ApiError(401, 'Session expired — sign in again.');
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const b = await res.json();
      if (b?.error) msg = b.error;
      else if (b?.message) msg = b.message;
    } catch { /* keep status text */ }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
